import React, { useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, ShieldAlert } from "lucide-react";
import type { ProjectMarginLeakageBreakdown } from "../models/production";
import { cn, formatCurrency } from "../utils";

function formatPercent(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  return `${Math.round(value * 10) / 10}%`;
}

function formatSignedCurrency(value?: number, currency?: string) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const formatted = formatCurrency(Math.abs(value), currency || "GBP");
  return value > 0 ? `+${formatted}` : value < 0 ? `-${formatted}` : formatted;
}

function toneClasses(tone: "positive" | "negative" | "neutral") {
  if (tone === "positive") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (tone === "negative") return "border-rose-200 bg-rose-50 text-rose-700";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function segmentFill(tone: "positive" | "negative" | "neutral") {
  if (tone === "positive") return "bg-emerald-500";
  if (tone === "negative") return "bg-rose-500";
  return "bg-slate-400";
}

function InlineMetric({ label, value, helper }: { label: string; value: string; helper: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{label}</div>
      <div className="mt-3 text-2xl font-semibold text-slate-900">{value}</div>
      <div className="mt-2 text-xs leading-5 text-slate-500">{helper}</div>
    </div>
  );
}

export function ProjectMarginBreakdown({
  breakdown,
  currency,
}: {
  breakdown?: ProjectMarginLeakageBreakdown;
  currency: string;
}) {
  const [selectedStepId, setSelectedStepId] = useState<string>("execution");

  const selectedStep = useMemo(
    () => breakdown?.waterfallSteps.find((step) => step.id === selectedStepId) || breakdown?.waterfallSteps[0],
    [breakdown, selectedStepId]
  );

  if (!breakdown) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="text-sm font-semibold text-slate-900">Margin Breakdown</div>
          <div className="mt-1 text-xs leading-5 text-slate-500">
            This waterfall appears once the project has enough task finance state to reconcile margin movement between stages.
          </div>
        </div>
        <div className="px-5 py-8 text-center text-sm text-slate-500">No margin leakage breakdown is available yet.</div>
      </div>
    );
  }

  const maxMagnitude = Math.max(
    ...breakdown.waterfallSteps.map((step) => Math.abs(step.stepType === "total" ? step.runningEnd || 0 : step.value || 0)),
    1
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-5 py-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900">Margin Breakdown</div>
            <div className="mt-1 text-xs leading-5 text-slate-500">
              This keeps the analytical view aligned to the same production story: start position, artist assigned, artist started work, over schedule, and task complete.
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className={cn("inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium", toneClasses(breakdown.validation.reconciled ? "positive" : "negative"))}>
              {breakdown.validation.reconciled ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {breakdown.validation.reconciled ? "Waterfall reconciled" : "Reconciliation check needed"}
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-700">
              <ShieldAlert className="h-4 w-4" />
              {breakdown.reliability || "low"} confidence
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-5 px-5 py-5">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
          <InlineMetric
            label="Start Position Margin"
            value={formatCurrency(breakdown.initialBidBaseline.grossMargin || 0, currency)}
            helper={breakdown.initialBidBaseline.helperText}
          />
          <InlineMetric
            label="Task Complete Margin"
            value={formatCurrency(breakdown.finalActual.grossMargin || 0, currency)}
            helper={breakdown.finalActual.helperText}
          />
          <InlineMetric
            label="Total Margin Change"
            value={formatSignedCurrency(breakdown.totalMarginChange, currency)}
            helper={`${formatPercent(breakdown.totalMarginChangePctOfInitial)} of initial bid margin.`}
          />
          <InlineMetric
            label="Effective Rate / Day"
            value={typeof breakdown.effectiveRatePerDay === "number" ? formatCurrency(breakdown.effectiveRatePerDay, currency) : "—"}
            helper="Bid value divided by bid days, saved for later pricing and rate-card comparison work."
          />
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(320px,0.9fr)]">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div className="mb-4 text-xs font-medium text-slate-500">
              Select a stage to inspect exactly where margin changed and why.
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
              {breakdown.waterfallSteps.map((step) => {
                const magnitude = Math.abs(step.stepType === "total" ? step.runningEnd || 0 : step.value || 0);
                const heightPct = Math.max(14, Math.round((magnitude / maxMagnitude) * 100));
                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => setSelectedStepId(step.id)}
                    className={cn(
                      "flex min-h-[280px] flex-col rounded-xl border p-3 text-left transition",
                      selectedStep?.id === step.id ? "border-slate-900 bg-white shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"
                    )}
                    title={`${step.label}: ${formatSignedCurrency(step.stepType === "delta" ? step.value : step.runningEnd, currency)}`}
                  >
                    <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{step.label}</div>
                    <div className="mt-2 text-sm font-semibold text-slate-900">
                      {formatSignedCurrency(step.stepType === "delta" ? step.value : step.runningEnd, currency)}
                    </div>
                    <div className="mt-4 flex flex-1 items-end">
                      <div className="flex h-[180px] w-full items-end justify-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
                        <div className="flex w-full max-w-[72px] flex-col justify-end">
                          {step.stepType === "delta" ? (
                            <>
                              <div className="mb-2 text-center text-[11px] text-slate-500">{formatCurrency(step.runningStart || 0, currency)}</div>
                              <div className={cn("w-full rounded-t-md", segmentFill(step.tone))} style={{ height: `${heightPct}%` }} />
                              <div className="mt-2 text-center text-[11px] text-slate-500">{formatCurrency(step.runningEnd || 0, currency)}</div>
                            </>
                          ) : (
                            <>
                              <div className={cn("w-full rounded-t-md", segmentFill(step.tone))} style={{ height: `${heightPct}%` }} />
                              <div className="mt-2 text-center text-[11px] text-slate-500">{formatCurrency(step.runningEnd || 0, currency)}</div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={cn("mt-4 inline-flex rounded-full border px-2.5 py-1 text-xs font-medium", toneClasses(step.tone))}>
                      {step.stepType === "delta" ? "Delta" : "Stage Total"}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Selected Step</div>
            <div className="mt-2 text-lg font-semibold text-slate-900">{selectedStep?.label || "—"}</div>
            <div className="mt-4 space-y-3 text-sm text-slate-600">
              <div>
                <span className="font-medium text-slate-900">Value:</span>{" "}
                {formatSignedCurrency(selectedStep?.stepType === "delta" ? selectedStep?.value : selectedStep?.runningEnd, currency)}
              </div>
              {selectedStep?.stepType === "delta" ? (
                <>
                  <div>
                    <span className="font-medium text-slate-900">From:</span> {formatCurrency(selectedStep.runningStart || 0, currency)}
                  </div>
                  <div>
                    <span className="font-medium text-slate-900">To:</span> {formatCurrency(selectedStep.runningEnd || 0, currency)}
                  </div>
                </>
              ) : null}
              <div>
                <span className="font-medium text-slate-900">Meaning:</span> {selectedStep?.description || "—"}
              </div>
            </div>

            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Rule-Based Insights</div>
              <div className="mt-3 space-y-3">
                {breakdown.insights.map((insight) => (
                  <div key={insight.id} className={cn("rounded-lg border p-3", toneClasses(insight.tone === "warning" ? "negative" : insight.tone === "positive" ? "positive" : "neutral"))}>
                    <div className="text-sm font-semibold">{insight.summary}</div>
                    <div className="mt-1 text-xs leading-5">{insight.helperText}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <div className="text-sm font-semibold text-slate-900">Margin Leakage Components</div>
            <div className="mt-1 text-xs leading-5 text-slate-500">
              Each delta is derived from the existing finance stages. This is the auditable readout underneath the simplified trigger model.
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50 text-left text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
                <tr>
                  <th className="px-4 py-3">Component</th>
                  <th className="px-4 py-3">Value</th>
                  <th className="px-4 py-3">% Impact</th>
                  <th className="px-4 py-3">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {[
                  breakdown.assignmentDelta,
                  breakdown.executionDelta,
                  breakdown.dataAdjustmentDelta,
                ].map((delta) => (
                  <tr key={delta.id}>
                    <td className="px-4 py-3 font-medium text-slate-900">{delta.label}</td>
                    <td className={cn("px-4 py-3 font-medium", delta.direction === "negative" ? "text-rose-700" : delta.direction === "positive" ? "text-emerald-700" : "text-slate-700")}>
                      {formatSignedCurrency(delta.value, currency)}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{formatPercent(delta.percentOfInitialMargin)}</td>
                    <td className="px-4 py-3 text-slate-600">{delta.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* TODO: extend this breakdown with departmental leakage, per-artist contributions,
            predictive margin risk, and exportable finance reporting once the first project-level
            waterfall is stable and trusted operationally. */}
      </div>
    </div>
  );
}
